'use client'

import { formatFontFamily } from '@/lib/themeHelpers'

interface AudienceSectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  content: any;
}


// SmallStats component for small stats display
function SmallStats({ theme, radius, stats }: { theme: any; radius: number; stats: any[] }) {
  if (!stats || stats.length === 0) return null;

  return (
    <div
      className="p-6"
      style={{
        backgroundColor: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: radius
      }}
    >
      <h3 className="text-xl font-semibold mb-6 font-primary" style={{ color: theme.text }}>
        Highlights
      </h3>
      
      <div className="grid grid-cols-2 gap-4">
        {stats.map((stat, index) => (
          <div key={index} className="text-center p-3" style={{
            backgroundColor: theme.surface === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
            borderRadius: radius / 2
          }}>
            <div className="text-2xl font-bold font-secondary" style={{ color: theme.text }}>
              {stat.value}
            </div>
            <div className="text-xs font-secondary" style={{ color: theme.mutedText }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AudienceSection({ 
  surface, 
  theme, 
  radius, 
  fontFamily, 
  gradStart, 
  gradEnd, 
  angle,
  content
}: AudienceSectionProps) {
  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  return (
    <section id="audience" className="relative w-full" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 font-primary" style={{ color: theme.text }}>
            {content.title}
          </h2>
          <p className="text-lg max-w-3xl mx-auto font-primary" style={{ color: theme.mutedText }}>
            {content.description}
          </p>
        </div>

        {/* Charts Section - Match original structure */}
        <div className="mb-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Demographics Chart - Only show first chart like original */}
            {content.demographicsCharts && content.demographicsCharts[0] && (
              <div
                className="p-6"
                style={{
                  backgroundColor: theme.card,
                  border: `1px solid ${theme.border}`,
                  borderRadius: radius
                }}
              >
                <h3 className="text-xl font-semibold mb-6 font-primary" style={{ color: theme.text }}>
                  {content.demographicsCharts[0].title}
                </h3>
                
                <div className="space-y-4">
                  {content.demographicsCharts[0].items.map((item: any, itemIndex: number) => (
                    <div key={itemIndex} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium font-secondary" style={{ color: theme.text }}>
                          {item.label}
                        </span>
                        <span className="text-sm font-semibold font-secondary" style={{ color: theme.text }}>
                          {item.percentage}%
                        </span>
                      </div>
                      
                      {/* Progress Bar - Match original styling */}
                      <div 
                        className="w-full h-2 rounded-full overflow-hidden"
                        style={{ backgroundColor: theme.input || theme.border }}
                      >
                        <div
                          className="h-full rounded-full transition-all duration-300"
                          style={{
                            backgroundColor: theme.accent,
                            width: `${item.percentage}%`
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Small Stats Component */}
            <SmallStats theme={theme} radius={radius} stats={content.smallStats} />
          </div>
        </div>

      </div>
    </section>
  );
}
